<template>
    <video crossOrigin="anonymous"
           v-bind:class="'video-js ' + video_layout_class"
           @timeupdate="onPlayerTimeupdate"
           controls preload playsinline="" webkit-playsinline="" id="videoTag" ref="videoPlayer">
        <source id="srcVideo" src="" type="video/mp4">
    </video>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue';
import { usePlayerStore } from '@/playerVue3/storage/storePlayer';
import { addCeroToNumber, timeFormat } from "@/playerVue3/services/misc-service";
import { ACTION_TYPE } from "@/playerVue3/enums/hotspot-enum";
import { ANALYTICS_EVENT_TYPE } from "@/playerVue3/enums/video-event-enum";
import { fetchVideoDimensions } from "@/playerVue3/services/video-service";
import { trackPlayerEvents, trackWatchedTime } from "@/playerVue3/services/analytic-service";
import { trackInteractionEvents } from "@/playerVue3/services/interactive-service";
import { debounce } from "lodash";
import { executeFullScreen } from "@/playerVue3/services/video-service";

// INITIALIZE STORE & COMPOSABLES
const store = usePlayerStore();

// PROPS & EMITS
const props = defineProps({
    options: {
        type: Object,
        required: true,
        default() {
            return {};
        }
    },
    video_layout_class: {
        type: String,
        required: true,
    },
    playerReady: {
        type: Boolean,
        required: true,
    }
});

const emit = defineEmits(['player-ready']);

// REACTIVE VARIABLES
const videoPlayer = ref(null);
const accuracyFrameCount = ref('');
const currentFrameInASecond = ref(0);
const currentFrameInASecondByTime = ref(0);
const currentTime = ref('');
const decodedFrameCount = ref("");
const droppedFrameCount = ref("");
const firstTimePause = ref(false);
const FPS = ref(props.options.fps);
const FRAME_WINDOW_LOWER = ref((1 / props.options.fps) * 0.95);
const FRAME_WINDOW_UPPER = ref((1 / props.options.fps) * 0.25);
const frameInMilliseconds = ref(1 / props.options.fps);
const lastDecodedFrameCount = ref(0);
const nextFrame = ref(1);
const nextFrameTime = ref(1 / props.options.fps);
const player = ref(null);
const presentedFrames = ref(0);
const starttime = ref(0);
const timeoutId = ref(null);
const timestamp = ref(0);
const timeDrift = ref(null);
const videoRequestAnimationId = ref(undefined);
const videoTag = ref('');

// COMPUTED PROPERTIES
const getActiveHotspot = computed(() => store.getActiveHotspot);

// METHODS
const syncCurrentTimePlayer = (currentTimePlayer) => {
    store.setCurrentTimePlayer(currentTimePlayer);
};

const calculateTimeFrame = (timestamp) => {
    timeoutId.value = setTimeout(function() {
        const videoRealTime = videoTag.value.currentTime;
        const actualMilliseconds = ~~((videoRealTime - ~~videoRealTime) * 1000);
        currentTime.value = timeFormat(videoRealTime);
        const actualFrame = Math.round(videoRealTime / frameInMilliseconds.value);

        if (decodedFrameCount.value) {
            const currentDecodedFrameCount = videoTag.value[decodedFrameCount.value];
            accuracyFrameCount.value = addCeroToNumber(currentDecodedFrameCount, 5);
            const diffDecodedFrameCount = currentDecodedFrameCount - lastDecodedFrameCount.value;
            const check = videoRealTime >= nextFrameTime.value - FRAME_WINDOW_LOWER.value &&
                         videoRealTime <= nextFrameTime.value + FRAME_WINDOW_UPPER.value;

            if (check && diffDecodedFrameCount > 0) {
                presentedFrames.value = nextFrame.value++;
                nextFrameTime.value = nextFrame.value * frameInMilliseconds.value;
            } else if (videoRealTime >= nextFrameTime.value && presentedFrames.value < nextFrame.value) {
                presentedFrames.value = actualFrame;
                nextFrame.value = presentedFrames.value + 1;
                nextFrameTime.value = nextFrame.value * frameInMilliseconds.value;
            }
            lastDecodedFrameCount.value = currentDecodedFrameCount;
            currentFrameInASecond.value = Math.floor(presentedFrames.value % FPS.value);
            currentFrameInASecondByTime.value = Math.floor(actualMilliseconds * frameInMilliseconds.value);
        } else {
            presentedFrames.value = actualFrame;
        }

        const realTimeCode = presentedFrames.value * frameInMilliseconds.value;
        const playerMetrics = {
            'accuracyFrameCountFormatted': accuracyFrameCount.value,
            'currentFrameInASecondByTime': currentFrameInASecondByTime.value,
            'currentFrameInASecondByPresentedFrames': currentFrameInASecond.value,
            'currentFrameInASecondByTimeExecuted': currentFrameInASecond.value,
            'currentTimeBySeconds': videoRealTime,
            'currentTimeFormatted': currentTime.value,
            'fps': FPS.value,
            'oneFrameMilliseconds': (frameInMilliseconds.value).toFixed(4),
            'framesByTimeExecutedFormatted': addCeroToNumber(presentedFrames.value, 5),
            'realTimeExecutedFormatted': timeFormat(realTimeCode),
        };
        store.setPlayerMetrics(playerMetrics);

        timestamp = timestamp || new Date().getTime();
        const runtime = timestamp - starttime.value;
        if (runtime < player.value.getDuration()) {
            videoRequestAnimationId.value = requestAnimationFrame(function(timestamp) {
                calculateTimeFrame(timestamp);
            });
        }
    }, 1000 / FPS.value);
};

// LIFECYCLE HOOKS
watch(currentTime, debounce(function () {
    const currentHotspot = store.getActiveHotspot;
    if (currentHotspot && Number(currentHotspot.hotspotAction.actionType.actionTypeId) === ACTION_TYPE.HYPERLINK) {
        store.addActiveHotspot(null);
    }
}, 1000, {
    leading: true,
    trailing: false,
}));

watch(getActiveHotspot, async (newVal) => {
    if (newVal === null) {
        await trackInteractionEvents('end', store);
    } else {
        await trackInteractionEvents('start', store);
    }
});

onMounted(async () => {
    await nextTick();
    const videoID = store.getVideoID;
    let aspectRatio = '16:9';
    //let videoFinalizado = false;

    if (props.options.orientationClass === 'video-layout-vertical') {
        aspectRatio = '9:16';
    }

    let arrSubtitles = [];
    if (store.getVideoData.subtitle && store.getVideoData.subtitle.length > 0) {
        store.getVideoData.subtitle.forEach(function (data, index) {
            arrSubtitles.push({
                "kind": "captions",
                "file": store.getPathLocalOrServer + "videos/subtitles/" + data.name,
                "label": data.language.description
            });
        });
    }

    /*let typeCampaignClassificationID = 0;
    if (store.getVideoData.typeCampaignClassification && store.getVideoData.typeCampaignClassification.typeCampaignClassificationId) {
        typeCampaignClassificationID = store.getVideoData.typeCampaignClassification.typeCampaignClassificationId;
    }*/
    player.value = jwplayer("videoTag").setup({
        "playlist": [{
            'aspectratio': aspectRatio,
            'controls': 'false',
            'dock': 'false',
            'file': props.options.src,
            'height': '100%',
            "mediaid": videoID,
            "renderCaptionsNatively": "true",
            'stretching': 'uniform',
            "title": props.options.title,
            "tracks": arrSubtitles,
            'width': '100%',
        }],
    });
    player.value.setControls(false);
    player.value.setCurrentCaptions(0);

    player.value.on('ready', function () {
        videoTag.value = document.getElementsByClassName("jw-video")[0];
        if (videoTag.value.webkitDecodedFrameCount !== undefined) {
            decodedFrameCount.value = "webkitDecodedFrameCount";
            droppedFrameCount.value = "webkitDroppedFrameCount";
        }

        videoTag.value.addEventListener("canplay", e => {
            if (timeDrift.value == null) {
                timeDrift.value = videoTag.value.currentTime;
            }
        });
        this.setMute(false);
        store.setPlayer(this);

        let result = fetchVideoDimensions($("#videoTag"));
        store.setOverlayLayoutCoordinates(result);
        store.setPlayerIsReady(true);

        this.on('time', function () {
            syncCurrentTimePlayer(this.getPosition());
        });

        this.on('play', function () {
            // Valida que el tipo de video sea de elearning
            /*if (typeCampaignClassificationID == 5 && videoFinalizado) {
                player.value.pause();
            }*/
            const currentQuality = store.getCurrentResolutionSelected;
            store.getPlayer.setCurrentQuality(currentQuality);
            if (!store.getVideoWasExecuted) {
                store.setVideoWasExecuted(true);
            }
            $('.thumbnail-item').hide();
            $('#bottom-bar-pause-button').show();
            $('#bottom-bar-play-button').hide();
            setTimeout(() => videoRequestAnimationId.value = requestAnimationFrame(function(){
                starttime.value = new Date().getTime();
                timestamp.value = new Date().getTime();
                calculateTimeFrame(timestamp.value);
            }), 0);
            setTimeout(() => trackWatchedTime(store, 'play', store.getTimePlayedStart, store.getPlayer.getPosition()), 0);
            setTimeout(() => trackPlayerEvents(store, ANALYTICS_EVENT_TYPE.PLAY), 0);
        });

        this.on('pause', function () {
            $('#bottom-bar-pause-button').hide();
            $('#bottom-bar-play-button').show();
            cancelAnimationFrame(videoRequestAnimationId.value);
            videoRequestAnimationId.value = undefined;
            setTimeout(() => trackWatchedTime(store, 'pause', store.getTimePlayedStart, store.getPlayer.getPosition()), 0);
            setTimeout(() => trackPlayerEvents(store, ANALYTICS_EVENT_TYPE.PAUSE), 0);
            clearTimeout(timeoutId.value);
        });

        this.on('complete', function () {
            //videoFinalizado = true;
            $('.thumbnail-item').show();
            $('#bottom-bar-pause-button').hide();
            $('#bottom-bar-play-button').show();
            cancelAnimationFrame(videoRequestAnimationId.value);
            videoRequestAnimationId.value = undefined;
            setTimeout(() => trackWatchedTime(store, 'complete', store.getTimePlayedStart, store.getPlayer.getPosition()), 0);
            setTimeout(() => trackPlayerEvents(store, ANALYTICS_EVENT_TYPE.COMPLETE), 0);
            clearTimeout(timeoutId.value);
        });

        const playerMetrics = {
            'accuracyFrameCountFormatted': accuracyFrameCount.value,
            'currentFrameInASecondByTime': currentFrameInASecondByTime.value,
            'currentFrameInASecondByTimeExecuted': currentFrameInASecond.value,
            'currentTimeBySeconds': 0,
            'currentTimeFormatted': currentTime.value,
            'fps': FPS.value,
            'oneFrameMilliseconds': (frameInMilliseconds.value).toFixed(4),
            'framesByTimeExecutedFormatted': addCeroToNumber(presentedFrames.value, 5),
            'realTimeExecutedFormatted': timeFormat(0),
        };
        store.setPlayerMetrics(playerMetrics);

        $(document).bind('fullscreenchange webkitfullscreenchange mozfullscreenchange msfullscreenchange', function (e) {
            let fullscreenElement = document.fullscreenElement || document.webkitFullscreenElement || document.mozFullscreenElement || document.msFullscreenElement;

            if (!fullscreenElement && store.getFullScreen) {
                executeFullScreen(store);
            }
        });
        //firstInteraction(store);

        const videoContainer = document.querySelector('#videoPlayerContainer');
        const jumpSeconds = videoContainer.dataset.sec;

        if (jumpSeconds && jumpSeconds > 0) {
            console.log(`Jumping to ${jumpSeconds} seconds`);
            player.value.seek(parseInt(jumpSeconds));
        }

        $('#loader-container').hide();
        emit('player-ready');
    });

    player.value.on('seeked', function () {
        if (firstTimePause.value === false) {
            firstTimePause.value = true;
        }
        store.setTimePlayedStart(this.getPosition());
        setTimeout(() => trackPlayerEvents(store, ANALYTICS_EVENT_TYPE.SEEKED), 0);
    });

    const videoData = store.getVideoData;
    if (videoData.videoWidth < videoData.videoHeight) {
        props.video_layout_class = 'video-layout-vertical';
    }
});

onBeforeUnmount(() => {
    if (player.value) {
        player.value.dispose();
    }
    if (videoRequestAnimationId.value) {
        cancelAnimationFrame(videoRequestAnimationId.value);
    }
    if (timeoutId.value) {
        clearTimeout(timeoutId.value);
    }
});
</script>
